import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, ArrowRight, Clock } from 'lucide-react';

const Footer = () => {
  return (
    <footer data-testid="main-footer" className="relative bg-slate-900 text-white overflow-hidden">
      {/* Decorative gradient orb */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-primary/15 rounded-full blur-[120px] pointer-events-none" />

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-8 md:gap-10">
          {/* Company Info */}
          <div className="col-span-2 sm:col-span-2 md:col-span-4 lg:col-span-4">
            <h3 className="text-xl md:text-2xl font-bold mb-3 tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>M DION <span className="text-primary">LIGHTING</span></h3>
            <p className="text-sm text-white/45 mb-4 font-light leading-relaxed max-w-sm">
              Certified Illumidel dealer bringing premium architectural and landscape lighting solutions.
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/20 border border-primary/30 rounded-full">
              <span className="text-xs font-semibold text-primary">Certified Illumidel Dealer</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-span-1 lg:col-span-2">
            <h4 className="text-xs font-semibold mb-5 tracking-widest uppercase text-white/35">Company</h4>
            <ul className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-white/40 hover:text-white/80 transition-colors duration-300 font-light flex items-center gap-1.5 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div className="col-span-1 lg:col-span-2">
            <h4 className="text-xs font-semibold mb-5 tracking-widest uppercase text-white/35">Resources</h4>
            <ul className="space-y-3">
              {[
                { name: 'Get a Quote', path: '/quote' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-white/40 hover:text-white/80 transition-colors duration-300 font-light flex items-center gap-1.5 group">
                    <ArrowRight className="w-3 h-3 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="col-span-2 sm:col-span-2 md:col-span-4 lg:col-span-4">
            <h4 className="text-xs font-semibold mb-5 tracking-widest uppercase text-white/35">Get in Touch</h4>
            <ul className="space-y-4">
              <li>
                <a href="mailto:mdionlighting@gmail.com" className="flex items-start gap-3 group" data-testid="footer-email">
                  <div className="w-9 h-9 rounded-lg bg-white/6 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 group-hover:border-primary/30 transition-all duration-300">
                    <Mail size={14} className="text-white/40 group-hover:text-primary" />
                  </div>
                  <div>
                    <span className="text-xs text-white/30 font-light">Email us</span>
                    <p className="text-sm text-white/60 group-hover:text-white/80 transition-colors font-light">mdionlighting@gmail.com</p>
                  </div>
                </a>
              </li>
              <li>
                <a href="tel:306-961-4333" className="flex items-start gap-3 group" data-testid="footer-phone">
                  <div className="w-9 h-9 rounded-lg bg-white/6 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 group-hover:border-primary/30 transition-all duration-300">
                    <Phone size={14} className="text-white/40 group-hover:text-primary" />
                  </div>
                  <div>
                    <span className="text-xs text-white/30 font-light">Call us</span>
                    <p className="text-sm text-white/60 group-hover:text-white/80 transition-colors font-light">306-961-4333</p>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/6 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <Clock size={14} className="text-white/40" />
                  </div>
                  <div>
                    <span className="text-xs text-white/30 font-light">Hours of Operation</span>
                    <p className="text-sm text-white/60 font-light">Monday - Sunday: 8:00 AM - 5:00 PM</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/25 font-light">
            &copy; {new Date().getFullYear()} M DION LIGHTING. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link to="/privacy" className="text-xs text-white/25 hover:text-white/50 transition-colors duration-300 font-light">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-xs text-white/25 hover:text-white/50 transition-colors duration-300 font-light">
              Terms of Service
            </Link>
            <Link to="/admin/login" data-testid="footer-admin-link" className="text-xs text-white/25 hover:text-white/50 transition-colors duration-300 font-light">
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
